import { Badge, Button, Col, Container, Row } from "react-bootstrap"
import { useLoaderData, useNavigate } from "react-router-dom"
import Flight, { FlightPostFlightData, flightNeedsTrainingRecord, formatCtot, getTrainingRecordInstructor } from "../../models/flight"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowLeft, faCircleUser, faLocationArrow } from '@fortawesome/free-solid-svg-icons'
import moment from "moment"
import { AviTracerApi } from "../../avitracerApi"
import { useRef, useState } from "react"
import { useAppDispatch } from "../../store/hooks"
import { pilotAcceptBriefing, refreshFlight } from "../../store/flightSlice"
import SignatureCanvas from 'react-signature-canvas'
import { useForm } from "react-hook-form"
import EntityInputBox from "../../components/EntityDetails/EntityInputBox"
import EntityDateTimePicker from "../../components/EntityDetails/EntityDateTimePicker"
import { getDisplayableTextFromFlightPlanPoint } from "../../models/flightPlan"
import { isFlightInsideGreece } from "../../utils/InsideGreeceUtils"
import { getCrewFullName } from "../../models/crew"
import { getPassengerFullName } from "../../models/passenger"
import IfpsStateBadge from "../../components/IfpsStateBadge"
import IfpsMessagesList from "../../components/IfpsMessagesList"
import { auth } from "../../firebase"
import { useAppSelector } from "../../store/hooks"
import { selectedOrganization } from "../../store/organizationsSlice"

function FlyFlightDetailsPage(){
    const navigate = useNavigate();
    const dispatch = useAppDispatch()

    const loadedFlight = useLoaderData() as Flight
    const selectedOrg = useAppSelector(selectedOrganization)
    
    const [flight, setFlight] = useState<Flight>(loadedFlight)
    const signedPack = flight.briefingPilotAccept !== undefined

    const isPic = flight.pic.uid !== undefined && flight.pic.uid === auth.currentUser?.uid
    //A last-minute change is always a revision of the planned W&B: added to the pack now if it exists,
    //otherwise when the pack is generated. It needs a planned W&B within limits to start from.
    const packReady = flight.briefing?.status === "ready"
    const canReviseWb = (isPic || selectedOrg?.role === "admin") && flight.wb?.status === "ok"
    const revisions = flight.wbRevisions ?? []

    //Post-flight student training record: the instructor (or an admin) fills it once the flight has departed
    const isTrainingInstructor = getTrainingRecordInstructor(flight)?.uid !== undefined && getTrainingRecordInstructor(flight)?.uid === auth.currentUser?.uid
    const showTrainingRecord = selectedOrg !== undefined && flightNeedsTrainingRecord(flight, selectedOrg)
      && (isTrainingInstructor || selectedOrg.role === "admin")
      && moment(flight.departureDateTime).isBefore(moment())
    const trainingRecordFilled = flight.customDocs?.trainingRecord !== undefined
    const trainingRecordSigned = flight.customDocs?.trainingRecordSigned !== undefined

    const downloadSignedTrainingRecordPressed = async () => {
      const pdfData = await AviTracerApi.getCustomPdfDoc(flight.id!, "trainingRecord", true)
      const link = document.createElement('a');
      link.download = flight.flightId + "-" + moment(flight.departureDateTime).utc().format("DD-MM-YY-HHmm") + "-trainingRecord.pdf";
      link.href = window.URL.createObjectURL(new Blob([pdfData], {type: 'application/pdf'}));
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
    }
    //a revision submitted after the pack was signed needs its own PIC signature
    const unsignedRevision = signedPack ? revisions.find((r) => r.signed === undefined) : undefined

    const downloadRevisionPressed = async (n: number) => {
      const pdfData = await AviTracerApi.getWbRevisionPdf(flight.id!, n)
      const pdfURL = window.URL.createObjectURL(new Blob([pdfData], {type: 'application/pdf'}))
      const link = document.createElement('a');
      link.download = flight.flightId + "-" + moment(flight.departureDateTime).utc().format("DD-MM-YY-HHmm") + `-wb-rev${n}.pdf`;
      link.href = pdfURL;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
    }

    const fplFile = flight.fplFile
    const ifrIfps = fplFile.status === "filed" || fplFile.status === "delayed" ? fplFile.ifps : undefined

    const downloadDocPressed = async (docName: string) => {
      const pdfData = await AviTracerApi.getPdfDoc(flight.id!, docName)
      const pdfBlob = new Blob([pdfData], {type: 'application/pdf'});
      var pdfURL = window.URL.createObjectURL(pdfBlob);
  
      const link = document.createElement('a');
      link.download = flight.flightId + "-" + moment(flight.departureDateTime).utc().format("DD-MM-YY-HHmm") + `-${docName}.pdf` ;
      link.href = pdfURL;
      link.target = '_blank';
      document.body.appendChild(link);
      
      link.click();
    }

    const flightPlanStatusElement = () => {
      if (flight.fplFile.status === "filed"){
        return (
          <span className="text-success fw-semibold">
            Filed
          </span>
        )
      }else if (flight.fplFile.status === "delayed"){
        return (
          <span className="text-success fw-semibold">
            Delayed for {moment(flight.fplFile.newTime).utc().format("HH:mm")}
          </span>
        )
      }else{
        return (
          <span className="text-danger fw-semibold">Not filed</span>
        )
      }
    } 

    const briefingPackStatusElement = () => {
      if (flight.briefing?.status === "ready"){
        return (
          <>
            {flight.briefingPilotAccept !== undefined ? (
              <span className="text-success fw-semibold">Signed</span>
            ) : (
              <span className="text-danger fw-semibold">Not signed</span>
            )}
            <div className="d-flex flex-column align-items-start">
              <Button disabled={!signedPack} variant="link" className="px-0 py-1" onClick={() => downloadDocPressed("briefing-signed")}>
                Signed Briefing Pack
              </Button>
              <Button variant="link" className="px-0 py-1" onClick={() => downloadDocPressed("briefing")}>
                Full Briefing Pack
              </Button>
              <Button variant="link" disabled={flight.wb?.status !== "ok"} className="px-0 py-1" onClick={() => downloadDocPressed("wb")}>
                Weight & Balance{revisions.length > 0 ? " (latest)" : ""}
              </Button>
              <Button variant="link" className="px-0 py-1" onClick={() => downloadDocPressed("navLog")}>
                Navigation Log
              </Button>
              <Button variant="link" className="px-0 py-1" onClick={() => downloadDocPressed("icaoFpl")}>
                ICAO Flight Plan
              </Button>
              <Button variant="link" className="px-0 py-1" onClick={() => downloadDocPressed("genDec")}>
                General Declaration
              </Button>
              {flight.isCostSharing !== undefined && isFlightInsideGreece(flight) && (
                <Button variant="link" className="px-0 py-1" onClick={() => downloadDocPressed("form731")}>
                  Form 731
                </Button>
              )}
            </div>
          </>
        );
      }else{
        return (
          <span className="text-danger fw-semibold">Not ready</span>
        )
      }
    } 


    const pilotAcceptBriefingPressed = async () => {
      if (signatureRef.current?.isEmpty()) {
        alert("Please sign in the above area.");
        return;
      }

      setIsAcceptingBriefing(true);

      const signatureDataUrl = signatureRef.current!.getTrimmedCanvas().toDataURL();

      try {
        const newFlight = await dispatch(pilotAcceptBriefing({ flightId: flight.id!, signatureBase64: signatureDataUrl, postFlightData })).unwrap();
        setFlight(newFlight);
      } catch (e: any) {
        alert(e.message);
        //The flight may have changed while signing (e.g. the pack was discarded): show it as it is now
        try {
          setFlight(await dispatch(refreshFlight(flight.id!)).unwrap());
        } catch {}
      } finally {
        signatureRef.current!.clear();
        setShowSignaturePad(false);
        setIsAcceptingBriefing(false);
      }
    }
    
    const [isAcceptingBriefing, setIsAcceptingBriefing] = useState<boolean>(false)
    const [showSingaturePad, setShowSignaturePad] = useState<boolean>(false)
    const signatureRef = useRef<SignatureCanvas>(null)

    const [postFlightData, setPostFlightData] = useState<FlightPostFlightData>({})

    const [showPostFlightFields, setShowPostFlightFields] = useState<boolean>(false)

    if (showSingaturePad) {
      return (
        <SignPackComponent
          signatureRef={signatureRef}
          isAcceptingBriefing={isAcceptingBriefing}
          pilotAcceptBriefingPressed={pilotAcceptBriefingPressed}
          setShowSignaturePad={setShowSignaturePad}
          setShowPostFlightFields={setShowPostFlightFields}
        />
      );
    }else if (showPostFlightFields) {
      return (
        <PostFlightFieldsComponent
          flight={flight}
          setShowPostFlightFields={setShowPostFlightFields}
          setShowSignaturePad={setShowSignaturePad}
          fuelUnit={flight.aircraft.fuelUnit}
          postFlightData={postFlightData}
          setPostFlightData={setPostFlightData}
        />
      )
    } else {
      return (
        <>
          <Container>
            <div className="fixed-top">
              <Container className="d-flex justify-content-between">
                <Button variant="light" className="bg-white border mt-3" onClick={() => navigate("/fly")}>
                  <FontAwesomeIcon icon={faArrowLeft} />
                </Button>
                <Button variant="light" className="border mt-3" onClick={() => navigate("./watch")}>
                  <FontAwesomeIcon icon={faLocationArrow} className="pe-2"/>
                  Live Tracking
                </Button>
              </Container>
            </div>
            <Row style={{ paddingTop: "65px" }}>
              <h1 className="fw-bold">
                {flight.flightId} {" - "} {moment(flight.departureDateTime).utc().format("HH:mm")}
              </h1>
            </Row>
            {flight.studentPilot !== undefined && (
              <Row className="py-2">
                <h2>Student</h2>
                <div>
                  <div className="d-flex align-items-center py-2 my-2">
                    <FontAwesomeIcon size="2x" color="#9B9B9B" icon={faCircleUser} className="pe-2" />
                    <span className="align-middle">
                      {getCrewFullName(flight.studentPilot)}
                    </span>
                  </div>
                </div>
              </Row>
            )}
            {flight.passengers.length > 0 && (
              <Row className="py-2">
                <h2>Passengers</h2>
                {flight.passengers.map((passenger) => {
                  return (
                    <div key={passenger.id!}>
                      <div className="d-flex justify-content-between">
                        <div>
                          <div className="d-flex align-items-center py-2 my-2">
                            <FontAwesomeIcon size="2x" color="#9B9B9B" icon={faCircleUser} className="pe-2" />
                            <span className="align-middle">{getPassengerFullName(passenger)}</span>
                          </div>
                        </div>
                        <div className="d-flex align-items-center">
                          {flight.noShows?.[passenger.id!] !== undefined ? (
                            <Badge bg={"danger"}>No show</Badge>
                          ) : (
                            flight.checkIns?.[passenger.id!] !== undefined && <Badge bg={"success"}>Boarded</Badge>
                          )}
                        </div>
                      </div>
                      <hr className="m-0" />
                    </div>
                  );
                })}
              </Row>
            )}
            <Row className="py-2">
              <Col xs={12}>
                <strong>Flight Plan:</strong> {flightPlanStatusElement()}
                {ifrIfps && <span className="ms-2"><IfpsStateBadge ifps={ifrIfps} /></span>}
              </Col>
              <Col xs={12}>
                <strong>Briefing Pack:</strong> {briefingPackStatusElement()}
              </Col>
            </Row>
            <Row className="py-2">
              <h2>Route</h2>

              <Col xs={12}>
                {flight.route && (
                  <>
                    {[flight.route.departure, ...(flight.route.waypoints ?? []), flight.route.destination].map((w) => w.name).join(" ➜ ")}
                  </>
                )}
                {flight.customRoute && (
                  <>
                    {[getDisplayableTextFromFlightPlanPoint(flight.customRoute.departure), 
                      ...(flight.customRoute.waypoints ?? []).map((w) => w.name),
                      getDisplayableTextFromFlightPlanPoint(flight.customRoute.destination)
                      ].join(" ➜ ")}
                  </>
                )}
              </Col>
            </Row>
            {(canReviseWb || revisions.length > 0) && (
              <Row className="py-2">
                <h2>Weight &amp; Balance</h2>
                <Col xs={12}>
                  {revisions.length > 0 && (
                    <>
                      <strong>Revisions:</strong>
                      {/* newest first, the same order as in the briefing pack */}
                      {[...revisions].reverse().map((r) => (
                        <div key={r.n} className="d-flex align-items-center gap-2 flex-wrap">
                          <Button variant="link" className="px-0 py-1" onClick={() => downloadRevisionPressed(r.n)}>
                            Revision {r.n}
                          </Button>
                          {r.signed !== undefined ? (
                            <Badge bg="success">Signed</Badge>
                          ) : !packReady ? (
                            <Badge bg="secondary">Added when the pack is generated</Badge>
                          ) : signedPack ? (
                            <Badge bg="warning" text="dark">Awaiting PIC signature</Badge>
                          ) : (
                            <Badge bg="warning" text="dark">Sign with the briefing pack</Badge>
                          )}
                        </div>
                      ))}
                    </>
                  )}
                  {canReviseWb && (
                    <Button variant="outline-dark" className="fw-bold mt-2" onClick={() => navigate("./wb-revision")}>
                      Last-minute change (fuel, no-shows, baggage)
                    </Button>
                  )}
                </Col>
              </Row>
            )}
            {showTrainingRecord && (
              <>
              <hr className="mt-3 mb-0" />
              <Row className="py-2">
                <Col xs={12}>
                  <h2 className="mb-1">Training Record</h2>
                  <div className="mb-2">
                    {trainingRecordSigned ? (
                      <Badge bg="success">Signed</Badge>
                    ) : trainingRecordFilled ? (
                      <Badge bg="warning" text="dark">Filled – awaiting FI signature</Badge>
                    ) : (
                      <Badge bg="secondary">Not filled</Badge>
                    )}
                  </div>
                  <div>
                    {trainingRecordSigned ? (
                      <Button variant="outline-dark" size="sm" className="fw-bold" onClick={downloadSignedTrainingRecordPressed}>
                        Download Training Record
                      </Button>
                    ) : trainingRecordFilled ? (
                      <Button variant="outline-dark" size="sm" className="fw-bold" onClick={() => navigate(`/fly/${flight.id}/docs/trainingRecord/sign`)}>
                        Sign Training Record
                      </Button>
                    ) : (
                      <Button variant="outline-dark" size="sm" className="fw-bold" onClick={() => navigate(`/fly/${flight.id}/docs/trainingRecord`)}>
                        Fill Training Record
                      </Button>
                    )}
                  </div>
                </Col>
              </Row>
              </>
            )}
            {ifrIfps && (
              <>
              <hr className="mt-3 mb-0" />
              <Row className="py-2">
                <h2 className="mb-1">EUROCONTROL IFPS</h2>
                {ifrIfps.ctot && (
                  <Col xs={12}>
                    <div className="border rounded text-center p-3 my-3" style={{ backgroundColor: "#fff8e5", borderColor: "#f0dfae" }}>
                      <div className="small" style={{ color: "#8a6d1f", letterSpacing: "1px" }}>CALCULATED TAKE-OFF TIME</div>
                      <div className="fw-bold" style={{ fontSize: "2.5rem", lineHeight: 1.1, color: "#d38b00" }}>{formatCtot(ifrIfps.ctot)}</div>
                      {ifrIfps.regulations && ifrIfps.regulations.length > 0 && (
                        <div className="small" style={{ color: "#8a6d1f" }}>Regulation {ifrIfps.regulations.join(", ")}</div>
                      )}
                    </div>
                  </Col>
                )}
                {ifrIfps.errors && ifrIfps.errors.length > 0 && (
                  <Col xs={12}>
                    <div className="border border-danger rounded p-2 my-2">
                      {ifrIfps.errors.map((e, i) => (
                        <div key={i} className="text-danger small">{e}</div>
                      ))}
                    </div>
                  </Col>
                )}
                <Col xs={12}>
                  <IfpsMessagesList ormHistory={ifrIfps.ormHistory} label="Messages from EUROCONTROL" className="mt-1" />
                </Col>
              </Row>
              </>
            )}
            <div style={{ height: unsignedRevision ? "290px" : "220px" }} />

            <div className="fixed-bottom bg-light">
              <div className="d-grid  gap-2 m-3 ">
                {unsignedRevision && isPic && (
                  <Button style={{ height: "60px" }} className="fw-bold" variant="warning" onClick={() => navigate(`./wb-revision?sign=${unsignedRevision.n}`)}>
                    Sign W&B Revision {unsignedRevision.n}
                  </Button>
                )}
                <div className="d-grid">
                  <Button
                    disabled={signedPack || !packReady || !isPic}
                    style={{ height: "60px" }}
                    className=" fw-bold"
                    variant="dark"
                    onClick={() => setShowPostFlightFields(true)}
                  >
                    {signedPack ? "Briefing Pack Signed" : "Sign Briefing Pack"}
                  </Button>
                  {/* why it can't be signed: only the PIC signs (the backend refuses anyone else), and only a generated pack */}
                  {!signedPack && (!packReady || !isPic) && (
                    <div className="small text-muted text-center mt-1">
                      {!packReady ? "The briefing pack needs to be generated first." : "Only the PIC can sign the briefing pack — you are not the PIC of this flight."}
                    </div>
                  )}
                </div>
                <Button
                  disabled={flight.passengers.length === 0}
                  style={{ height: "60px", backgroundColor: "#f7f7f7", borderColor: "#dfdfdf" }}
                  className="text-dark fw-bold"
                  onClick={() => navigate("./scan")}
                >
                  Scan Passengers
                </Button>
              </div>
            </div>
          </Container>
        </>
      );
    }
}

export default FlyFlightDetailsPage

interface SignPackComponentProps {
  setShowSignaturePad: (show: boolean) => void;
  setShowPostFlightFields: (show: boolean) => void;
  pilotAcceptBriefingPressed: () => void;
  isAcceptingBriefing: boolean;
  signatureRef: React.RefObject<SignatureCanvas>
}

function SignPackComponent({ setShowSignaturePad, pilotAcceptBriefingPressed, isAcceptingBriefing, signatureRef, setShowPostFlightFields }: SignPackComponentProps) {
  return (
    <>
      <div className="fixed-top">
        <Container>
          <Button
            disabled={isAcceptingBriefing}
            variant="light"
            className="bg-white border mt-3"
            onClick={() => {setShowPostFlightFields(true); setShowSignaturePad(false)}}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </Button>
        </Container>
      </div>
      <div className="vw-100 vh-100 bg-white">
        <div className="bg-white w-100 h-100">
          <SignatureCanvas minWidth={2} maxWidth={5} ref={signatureRef} dotSize={3} canvasProps={{ className: "w-100 h-100" }} />
        </div>
      </div>
      <div className="fixed-bottom bg-light">
        <div className="d-flex">
          <div className="w-25 d-grid  gap-2 m-2">
            <Button
              disabled={isAcceptingBriefing}
              style={{ height: "60px" }}
              variant="light"
              className="fw-bold"
              onClick={() => {
                signatureRef.current!.clear();
              }}
            >
              Clear
            </Button>
          </div>
          <div className="w-75 d-grid gap-2 m-2">
            <Button
              disabled={isAcceptingBriefing}
              style={{ height: "60px" }}
              className="fw-bold"
              variant="dark"
              onClick={pilotAcceptBriefingPressed}
            >
              {isAcceptingBriefing ? "Saving..." : "Sign Briefing Pack"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

interface PostFlightFieldsComponentProps {
  flight: Flight
 setShowSignaturePad: (show: boolean) => void;
 setShowPostFlightFields: (show: boolean) => void;
 fuelUnit: string
 postFlightData: FlightPostFlightData
 setPostFlightData: React.Dispatch<React.SetStateAction<FlightPostFlightData>>
}

function PostFlightFieldsComponent({ flight, setShowSignaturePad, setShowPostFlightFields, fuelUnit, postFlightData, setPostFlightData }: PostFlightFieldsComponentProps) {

  const { control, watch } = useForm<FlightPostFlightData>({defaultValues: postFlightData});

  watch((data) => {
    setPostFlightData(data as FlightPostFlightData);
  })

  return (
    <>
      <div className="fixed-top">
        <Container>
          <Button variant="light" className="bg-white border mt-3" onClick={() => setShowPostFlightFields(false)}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </Button>
        </Container>
      </div>
      <Container style={{ paddingTop: "80px", paddingBottom:"100px" }}>
        <Row className="gy-2">
          <Col xs={12}>
            <h2>Actual Fuel</h2>
          </Col>
          <Col xs={12}>
            <EntityInputBox
              control={control}
              displayInfoInput={{
                key: "blockOffFuel",
                name: `Block Off Fuel (${fuelUnit})`,
                type: "number",
              }}
            />
          </Col>
          <Col xs={12}>
            <EntityInputBox
              control={control}
              displayInfoInput={{
                key: "takeoffFuel",
                name: `Take Off Fuel (${fuelUnit})`,
                type: "number",
              }}
            />
          </Col>
          <Col xs={12}>
            <EntityInputBox
              control={control}
              displayInfoInput={{
                key: "landingFuel",
                name: `Landing Fuel (${fuelUnit})`,
                type: "number",
              }}
            />
          </Col>
          <Col xs={12}>
            <EntityInputBox
              control={control}
              displayInfoInput={{
                key: "blockOnFuel",
                name: `Block On Fuel (${fuelUnit})`,
                type: "number",
              }}
            />
          </Col>
          </Row>
          <Row className="gy-2 pt-5">
          <Col xs={12}>
            <h2>Actual Flight Time</h2>
          </Col>
          <Col xs={12}>
            <EntityDateTimePicker
              control={control}
              displayInfoInput={{
                key: "blockOffTime",
                suggestedValue: flight.departureDateTime,
                name: "Block Off Time",
                timeName: "Block Off Time",
                type: "dateTime",
                useUtc: true,
                showOnlyTimePicker: true,
                timeFlatpickrOptions: {
                  dateFormat: "H:i",
                  time_24hr: true,
                  minuteIncrement: 1,
                },
              }}
            />
          </Col>
          <Col xs={12}>
            <EntityDateTimePicker
              control={control}
              displayInfoInput={{
                key: "takeoffTime",
                suggestedValue: moment(flight.departureDateTime).add(5, "minutes").toDate(),
                name: "Take off Time",
                timeName: "Take off Time",
                type: "dateTime",
                useUtc: true,
                showOnlyTimePicker: true,
                timeFlatpickrOptions: {
                  dateFormat: "H:i",
                  time_24hr: true,
                  minuteIncrement: 1,
                },
              }}
            />
          </Col>
          <Col xs={12}>
            <EntityDateTimePicker
              control={control}
              displayInfoInput={{
                key: "landingTime",
                suggestedValue: moment(flight.departureDateTime).add(flight.totalEETMins + 5, "minutes").toDate(),
                name: "Landing Time",
                timeName: "Landing Time",
                type: "dateTime",
                useUtc: true,
                showOnlyTimePicker: true,
                timeFlatpickrOptions: {
                  dateFormat: "H:i",
                  time_24hr: true,
                  minuteIncrement: 1,
                },
              }}
            />
          </Col>
          <Col xs={12}>
            <EntityDateTimePicker
              control={control}
              displayInfoInput={{
                key: "blockOnTime",
                suggestedValue: moment(flight.departureDateTime).add(flight.totalEETMins + 10, "minutes").toDate(),
                name: "Block On Time",
                timeName: "Block On Time",
                type: "dateTime",
                useUtc: true,
                showOnlyTimePicker: true,
                timeFlatpickrOptions: {
                  dateFormat: "H:i",
                  time_24hr: true,
                  minuteIncrement: 1,
                },
              }}
            />
          </Col>
        </Row>
      </Container>
      <div className="fixed-bottom bg-light">
        <div className="d-grid gap-2 m-2">
          <Button
            style={{ height: "60px" }}
            className="fw-bold"
            variant="dark"
            onClick={() => {
              setShowSignaturePad(true);
              setShowPostFlightFields(false);
            }}
          >
            Next
          </Button>
        </div>
      </div>
    </>
  );
}
